/**
 * SessionContext Utilities - Pure Helper Functions
 *
 * Purpose:
 * Provides pure utility functions extracted from SessionContext.jsx for pathname checking and
 * localStorage migration. These functions have no React dependencies, no side effects beyond
 * deterministic localStorage reads/writes, and can be used independently of the SessionContext.
 *
 * High-level Workflow:
 * 1. shouldSkipPropertiesFetch: Checks if a pathname should skip properties fetch (unauthenticated paths)
 * 2. migrateSelectedListingId: Migrates legacy localStorage key (selectedPropertyId → owner_selected_listing_id)
 *
 * Assumptions & Invariants:
 * - shouldSkipPropertiesFetch: pathname is a string or undefined/null
 * - migrateSelectedListingId: localStorage is available (checked internally)
 * - Both functions are pure (no side effects beyond localStorage for migration)
 * - Functions are deterministic (same input → same output)
 *
 * Dependencies:
 * Internal:
 *   - None (pure utilities)
 * External:
 *   - Browser localStorage API (for migrateSelectedListingId)
 *
 * Version: 1.0.0
 *
 * Changelog:
 * - 2026-01-26: Phase 2 - Extracted pure utilities from SessionContext.jsx (verbatim, no logic changes)
 */

/** Paths where we skip properties fetch (unauthenticated/public). */
const SKIP_PROPERTIES_PATHS = ['/login', '/owner/reset-password', '/forgot-password'];

/**
 * shouldSkipPropertiesFetch - Check if pathname should skip properties fetch
 * 
 * Intent: Determines if properties fetch should be skipped for a given pathname.
 * Used to avoid unnecessary API calls on unauthenticated/public pages.
 * 
 * @param {string|undefined|null} pathname - The pathname to check
 * @returns {boolean} True if properties fetch should be skipped, false otherwise
 */
export function shouldSkipPropertiesFetch(pathname) {
  return typeof pathname === 'string' && SKIP_PROPERTIES_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'));
}

/**
 * migrateSelectedListingId - Migrate legacy localStorage key to new key
 * 
 * Intent: Migrates the legacy localStorage key 'selectedPropertyId' to the new key
 * 'owner_selected_listing_id'. Handles both numeric and non-numeric legacy values.
 * Cleans up legacy key after migration.
 * 
 * Migration Logic:
 * 1. Try new key first (owner_selected_listing_id) - if valid, clean up legacy key
 * 2. Try legacy key (selectedPropertyId) - if numeric, migrate to new key and clean up legacy
 * 3. If legacy value is non-numeric, delete it and return null
 * 
 * @returns {number|null} The migrated listing ID (numeric) or null if no valid value found
 */
export function migrateSelectedListingId() {
  if (typeof window === 'undefined') return null;
  
  // 1. Try new key first
  const newKeyValue = localStorage.getItem('owner_selected_listing_id');
  if (newKeyValue) {
    const numId = parseInt(newKeyValue, 10);
    if (!isNaN(numId) && numId > 0) {
      // New key is valid, clean up legacy key if it exists
      localStorage.removeItem('selectedPropertyId');
      return numId;
    }
  }
  
  // 2. Try legacy key
  const legacyKeyValue = localStorage.getItem('selectedPropertyId');
  if (legacyKeyValue) {
    const numId = parseInt(legacyKeyValue, 10);
    if (!isNaN(numId) && numId > 0) {
      // Legacy value is numeric => migrate to new key
      localStorage.setItem('owner_selected_listing_id', String(numId));
      localStorage.removeItem('selectedPropertyId');
      return numId;
    } else {
      // Legacy value is non-numeric (e.g., "prop-1") => delete it
      localStorage.removeItem('selectedPropertyId');
      return null;
    }
  }
  
  return null;
}
